import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import {
  PRODUCTS,
  BULK_OFFERS,
  getPaidBoxCount,
  qualifiesForFreeEngraving,
  qualifiesForFreeBox,
  getCartSubtotal,
} from '../data/products';

const CartContext = createContext(null);

const applyBulkPromotions = (items) => {
  const paidBoxes = getPaidBoxCount(items);
  const withoutGifts = items.filter((item) => !item.isFreeGift);
  let next = withoutGifts.map((item) => ({
    ...item,
    freeEngraving: qualifiesForFreeEngraving(withoutGifts),
  }));

  if (paidBoxes >= BULK_OFFERS.freeBoxMinBoxes) {
    const freeProduct = PRODUCTS.find((p) => p.id === BULK_OFFERS.freeBoxProductId);
    if (freeProduct) {
      const existingGift = items.find((item) => item.isFreeGift);
      next = [
        ...next,
        existingGift || {
          product: freeProduct,
          quantity: 1,
          engravingData: null,
          subscription: null,
          isFreeGift: true,
          freeEngraving: false,
        },
      ];
    }
  }

  return next;
};

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState([]);
  const [isCartOpen, setIsCartOpen] = useState(false);

  const syncPromotions = useCallback((items) => applyBulkPromotions(items), []);

  const setCartWithPromotions = (updater) => {
    setCartItems((prev) => {
      const raw = typeof updater === 'function' ? updater(prev) : updater;
      return syncPromotions(raw);
    });
  };

  const addToCart = (product, quantity, engravingData, subscription, selectedOptions = null) => {
    setCartWithPromotions((prev) => {
      const existing = prev.findIndex(
        (item) =>
          !item.isFreeGift &&
          item.product.id === product.id &&
          item.subscription?.value === subscription?.value &&
          JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)
      );
      if (existing !== -1) {
        const updated = [...prev.filter((item) => !item.isFreeGift)];
        const idx = updated.findIndex(
          (item) =>
            item.product.id === product.id &&
            item.subscription?.value === subscription?.value &&
            JSON.stringify(item.selectedOptions) === JSON.stringify(selectedOptions)
        );
        updated[idx] = {
          ...updated[idx],
          quantity: updated[idx].quantity + quantity,
          engravingData: engravingData || updated[idx].engravingData,
          subscription: subscription || updated[idx].subscription,
        };
        return updated;
      }
      return [
        ...prev.filter((item) => !item.isFreeGift),
        { product, quantity, engravingData, subscription, selectedOptions, isFreeGift: false, freeEngraving: false },
      ];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (index) => {
    setCartWithPromotions((prev) => prev.filter((_, i) => i !== index));
  };

  const updateQuantity = (index, quantity) => {
    if (quantity < 1) return;
    setCartWithPromotions((prev) =>
      prev.map((item, i) => (i === index && !item.isFreeGift ? { ...item, quantity } : item))
    );
  };

  const clearCart = () => setCartItems([]);

  useEffect(() => {
    setCartItems((prev) => {
      const next = syncPromotions(prev);
      if (JSON.stringify(next) !== JSON.stringify(prev)) return next;
      return prev;
    });
  }, [syncPromotions]);

  const paidBoxes = getPaidBoxCount(cartItems);
  const totalBoxes = cartItems.reduce((sum, item) => sum + item.quantity, 0);
  const subtotal = getCartSubtotal(cartItems);
  const hasFreeEngraving = qualifiesForFreeEngraving(cartItems);
  const hasFreeBox = qualifiesForFreeBox(cartItems);

  return (
    <CartContext.Provider
      value={{
        cartItems,
        addToCart,
        removeFromCart,
        updateQuantity,
        clearCart,
        totalBoxes,
        paidBoxes,
        subtotal,
        hasFreeEngraving,
        hasFreeBox,
        isCartOpen,
        setIsCartOpen,
      }}
    >
      {children}
    </CartContext.Provider>
  );
};

export const useCart = () => {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be used inside CartProvider');
  return ctx;
};
